// 관리자 로그인 화면.
// 사용자(손님) 로그인과 분리되어 별도 도메인/콘솔에서 운영된다 (작업계획서 5장).
// changePasswordRequired 가 true 인 경우 /change-password 로 강제 이동시킨다.

import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Input } from "@oo/ui";
import { loginAdmin } from "@/features/auth/authApi.ts";
import { ApiError } from "@/api/client.ts";
import { useAdminAuthStore } from "@/stores/adminAuthStore.ts";

interface LocationState {
  from?: string;
}

// 관리자 로그인 화면 컴포넌트.
// 성공 시 from 경로로 복귀, 없으면 / 로 이동.
export default function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = (location.state as LocationState | null)?.from ?? "/";

  const setSession = useAdminAuthStore((s) => s.setSession);

  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (loginId.length < 3 || password.length < 1) {
      setError("아이디와 비밀번호를 모두 입력해 주세요.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await loginAdmin(loginId, password);
      setSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        refreshExpiresAt: result.refreshExpiresAt,
        expiresAtSec: result.expiresAtSec,
        admin: result.admin,
        changePasswordRequired: result.changePasswordRequired,
      });
      // 첫 시드 로그인 등 비밀번호 변경 필요 케이스는 다른 화면 진입을 차단하고 변경 화면으로 보낸다.
      if (result.changePasswordRequired) {
        navigate("/change-password", { replace: true });
        return;
      }
      navigate(fromPath, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 px-6 py-10">
      <header className="flex flex-col gap-2 text-center">
        <h1 className="text-2xl font-bold text-brand-700">열린축산 관리자</h1>
        <p className="text-base text-ink-600">관리자 ID 와 비밀번호로 로그인하세요.</p>
      </header>

      <form
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-4 rounded-lg bg-surface p-6 shadow-card"
      >
        <Input
          label="관리자 ID"
          name="loginId"
          type="text"
          autoComplete="username"
          value={loginId}
          onChange={(e) => setLoginId(e.target.value.trim())}
        />
        <Input
          label="비밀번호"
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={error ?? undefined}
        />
        <Button
          type="submit"
          fullWidth
          loading={submitting}
          disabled={loginId.length < 3 || password.length < 1}
        >
          {submitting ? "확인 중..." : "로그인"}
        </Button>
      </form>
    </main>
  );
}
